import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { CaseStudy } from "@/lib/caseStudy";

export type ProjectStatus = "draft" | "published";

export type { CaseStudy } from "@/lib/caseStudy";

export interface Project {
  id: string;
  title: string;
  description: string;
  image_url: string | null;
  technologies: string[];
  project_url: string | null;
  github_url: string | null;
  featured: boolean;
  display_order: number;
  status: ProjectStatus;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  case_study: CaseStudy | null;
  slug: string | null;
}

export interface CreateProjectData {
  title: string;
  description: string;
  image_url?: string;
  technologies?: string[];
  project_url?: string;
  github_url?: string;
  featured?: boolean;
  display_order?: number;
  status?: ProjectStatus;
  slug?: string | null;
  case_study?: CaseStudy;
}

export type UpdateProjectData = Partial<CreateProjectData>;

// Hook para listar projetos públicos
export function usePublicProjects() {
  return useQuery({
    queryKey: ["public-projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects" as never)
        .select("*")
        .eq("status", "published")
        .is("deleted_at", null)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as Project[];
    },
  });
}

// Hook para listar todos os projetos (admin) ou apenas publicados
export function useProjects(publishedOnly = false) {
  return useQuery({
    queryKey: ["projects", publishedOnly],
    queryFn: async () => {
      let query = supabase
        .from("projects" as never)
        .select("*")
        .is("deleted_at", null)
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (publishedOnly) {
        query = query.eq("status", "published");
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Project[];
    },
  });
}

// Hook para obter um projeto específico
export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("projects" as never)
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as Project;
    },
    enabled: !!id,
  });
}

// Hook para criar projeto
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectData) => {
      const { data: project, error } = await supabase
        .from("projects" as never)
        .insert({
          title: data.title,
          description: data.description,
          image_url: data.image_url || null,
          technologies: data.technologies || [],
          project_url: data.project_url || null,
          github_url: data.github_url || null,
          featured: data.featured || false,
          display_order: data.display_order || 0,
          status: data.status || "draft",
          slug: data.slug || null,
          case_study: data.case_study ?? {},
        } as never)
        .select()
        .single();

      if (error) throw error;
      return project as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["public-projects"] });
      toast.success("Projeto criado com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao criar projeto: " + error.message);
    },
  });
}

// Hook para atualizar projeto
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProjectData }) => {
      const { data: project, error } = await supabase
        .from("projects" as never)
        .update(data as never)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return project as Project;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["public-projects"] });
      queryClient.invalidateQueries({ queryKey: ["project", variables.id] });
      toast.success("Projeto atualizado com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao atualizar projeto: " + error.message);
    },
  });
}

// Hook para deletar projeto (soft delete)
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("projects" as never)
        .update({ deleted_at: new Date().toISOString() } as never)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["public-projects"] });
      toast.success("Projeto removido com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao remover projeto: " + error.message);
    },
  });
}

// Hook para alternar status publicado/rascunho
export function useToggleProjectStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      currentStatus,
    }: {
      id: string;
      currentStatus: ProjectStatus | null;
    }) => {
      const newStatus = currentStatus === "published" ? "draft" : "published";

      const { data: project, error } = await supabase
        .from("projects" as never)
        .update({ status: newStatus } as never)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return project as Project;
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["public-projects"] });
      toast.success(
        project.status === "published"
          ? "Projeto publicado com sucesso!"
          : "Projeto movido para rascunho.",
      );
    },
    onError: (error) => {
      toast.error("Erro ao alterar status: " + error.message);
    },
  });
}

// Hook para alternar destaque
export function useToggleProjectFeatured() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      currentFeatured,
    }: {
      id: string;
      currentFeatured: boolean | null;
    }) => {
      const { data: project, error } = await supabase
        .from("projects" as never)
        .update({ featured: !currentFeatured } as never)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return project as Project;
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["public-projects"] });
      toast.success(
        project.featured ? "Projeto marcado como destaque!" : "Projeto removido dos destaques.",
      );
    },
    onError: (error) => {
      toast.error("Erro ao alterar destaque: " + error.message);
    },
  });
}

// Hook para estatísticas de projetos
export function useProjectStats() {
  return useQuery({
    queryKey: ["project-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects" as never)
        .select("status, featured")
        .is("deleted_at", null);

      if (error) throw error;

      const projects = (data || []) as { status: ProjectStatus; featured: boolean }[];
      const total = projects.length;
      const published = projects.filter((p) => p.status === "published").length;
      const draft = projects.filter((p) => p.status === "draft").length;
      const featured = projects.filter((p) => p.featured).length;

      return {
        total,
        published,
        draft,
        featured,
      };
    },
  });
}
